<template>
  <div class="row condition-row">
    <div class="col-1 head">
      <template v-if="isFirstRule">
        <strong>If</strong>
      </template>
      <template v-else>
        <and-or-select v-model="ruleSet.join_type" :outside-modal="outsideModal"></and-or-select>
      </template>
    </div>
    <div class="col">
      <div class="condition-block" :class="outsideModal ? 'p-0' : 'scroll-area'">
        <referenced-controls-select
            class="conditional-rule-control-select"
            v-model="referencedControlId"
            :parent="parent"
            :id="'select-input-' + index"
            :outside-modal="outsideModal"
            :include-built-in-properties="includeBuiltInProperties"
            :key="referencedControlId"></referenced-controls-select>

        <template v-if="referencedControl && referencedControl.isValidForConditionalLogic">
          <template v-if="targetTypes.length > 1" >
            's
            <target-type-select :target-types="targetTypes" v-model="targetType"></target-type-select>
          </template>
          <comparison-select
              v-model="rule.comparison"
              :type="comparisonType"
              :show-is-updated-comparison="showIsUpdatedComparison"
              :show-previous-value-comparison-options="showPreviousValueComparisonOptions"
              :hide-datetime-is-within="hideDatetimeIsWithin"
              :show-presence-based-comparisons="showPresenceBasedComparisons"
              :outside-modal="outsideModal"
              :key="`${comparisonType}${rule.comparison}`"></comparison-select>
          <div v-if="rule.isValueRequired">
            <template v-if="matchFormFields">
              <div class="sr-only">
                <label for="form-field-select" class="a-select-label">Form Field Select</label>
              </div>
              <a-select
                id="form-field-select"
                v-model="rule.value"
                :options="targetReferencedControlOptions"
                > 
              </a-select>
            </template>
            <input v-else-if="comparisonType === ComparisonType.text" type="text" v-model="rule.value" class="xs">
            <input v-else-if="comparisonType === ComparisonType.numeric"
                   type="number" v-model="rule.value" class="xs">
            <input v-else-if="comparisonType === ComparisonType.instanceCount"
                   type="number" v-model="rule.value" class="xs" min="0">
            <referenced-control-options-select
                v-else-if="[ComparisonType.singleChoice, ComparisonType.multiChoice].includes(comparisonType)"
                type="number"
                v-model="rule.value"
                :referenced-control="referencedControl"
                :outside-modal="outsideModal"
                :key="`${rule.client_id}-control-options-select`"
            ></referenced-control-options-select>
            <template
              v-else-if="comparisonType === ComparisonType.datetime"
            >
              <div
                v-if="rule.comparison === DatetimeComparison.isWithin"
                class="d-flex"
              >
                <number-input
                  v-model="rule.value"
                  :min="1"
                  :allow-negatives="false"
                  :replacement-value="1"
                  class="mr-3"
                />
                <a-select
                  v-model="rule.value_duration_unit"
                  :options="DurationAndTimeHelper.durationUnitLongOptions"
                  :has-teleport="true"
                  :key="`${rule.client_id}-duration-unit-select`"
                />
              </div>
              <div v-else class="d-flex">
                <a-select
                  v-model="rule.date_type"
                  :options="dateTypeOptions"
                  :has-teleport="true"
                  :key="`${rule.client_id}-date-type-select`"
                />
                <date-picker
                  v-if="rule.date_type === 'static'"
                  :mode="referencedControl.getConditionDateTimeMode()"
                  :min="referencedControl.getConditionMinDateTime()" :max="referencedControl.getConditionMaxDateTime()"
                  v-model="rule.value"
                />
                <div v-else class="d-flex">
                  <a-select
                    v-model="rule.relative_date_type"
                    :options="relativeDateTypeOptions"
                    :has-teleport="true"
                    :key="`${rule.client_id}-relative-date-type-select`"
                  />
                  <a-select
                    v-model="rule.relative_date"
                    :options="relativeDateOptions"
                    :has-teleport="true"
                    :key="`${rule.client_id}-relative-date-select`"
                  />
                </div>
              </div>
            </template>
            <template v-else-if="comparisonType === ComparisonType.relativeUser">
              <referenced-control-options-select
                type="number"
                v-model="rule.value"
                :referenced-control="referencedControl"
                :outside-modal="outsideModal"
                :key="comparisonType"></referenced-control-options-select>
            </template>
            <template v-else-if="comparisonType === ComparisonType.accountRelation">
              <a-select
                v-model="rule.value"
                :options="accountRelationOptions"
              ></a-select>
            </template>
            <template v-else-if="comparisonType === ComparisonType.accountRelationUser">
              <conditional-rule-user-search
                :value="rule.value"
                :referenced-control="referencedControl"
                :relative-user-text="relativeUserText"
                :referenced_value_id="rule.referenced_value_id"
                @changed="selectedUserChange"></conditional-rule-user-search>
            </template>
          </div>
        </template>

        <div class="cta" v-if="!disallowMultipleConditions" style="margin-right: 15px;">
          <suffix>
            <help-tooltip v-if="comparisonType === ComparisonType.numeric" text="values for percentage formatted fields should be input as decimals. ex: min/max of 20%-100% should be input as .2 and 1"></help-tooltip>
          </suffix>
          <slot name="rule-options" :rule="rule">
            <rule-options :model="rule"></rule-options>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import ComparisonSelect from "./vSelects/comparisonSelect.vue";
  import AndOrSelect from "./vSelects/andOrSelect.vue";
  import TargetTypeSelect from "./vSelects/targetTypeSelect.vue";
  import {ComparisonType} from "../../models/conditionalRules/comparisonType";
  import ReferencedControlsSelect from "./vSelects/referencedControlsSelect.vue";
  import ReferencedControlOptionsSelect from "./vSelects/referencedControlOptionsSelect.vue";
  import DatePicker from "../datePicker.vue";
  import {TargetType} from "../../models/conditionalRules/targetType";
  import RuleOptions from "./ruleOptions.vue"
  import IConditionalRuleContainer from "../../models/conditionalRules/conditionalRuleContainer";
  import ConditionalRule from "../../models/conditionalRules/conditionalRule";
  import ConditionalRuleSet from "../../models/conditionalRules/conditionalRuleSet";
  import organizationMixin from "~/mixins/organizationMixin";
  import {DatetimeComparison} from "~/models/conditionalRules/datetimeComparison";
  import DurationAndTimeHelper from "~/helpers/durationAndTimeHelper";
  import {IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
  import ObjectModelRelationDefinition from "~/models/objectModelRelations/objectModelRelationDefinition";
  import {NumberHelper} from "~/helpers/numberHelper";
  import ConditionalRuleUserSearch from "./conditionalRuleUserSearch.vue";
  import ListView from "~/models/listViews/listView";

  export default Vue.extend({
    components: {
      DatePicker,
      ReferencedControlsSelect,
      ReferencedControlOptionsSelect,
      ComparisonSelect,
      AndOrSelect,
      TargetTypeSelect,
      RuleOptions,
      ConditionalRuleUserSearch,
    },
    mixins: [organizationMixin],
    props: {
      parent: Object as () => IConditionalRuleContainer,
      rule: Object as () => ConditionalRule,
      isFirstRule: Boolean,
      index: Number,
      ruleSet: Object as () => ConditionalRuleSet,
      disallowMultipleConditions: {
        type: Boolean,
        default: false,
      },
      includeBuiltInProperties:{
        type: Boolean,
        default: false,
      },
      includePaymentStepBuiltInProperties: {
        type: Boolean,
        default: false
      },
      showIsUpdatedComparison:{
        type: Boolean,
        default: false
      },
      showPresenceBasedComparisons: {
        type: [Boolean, Function],
        default: false,
      },
      showPreviousValueComparisonOptions:{
        type: Boolean,
        default: false
      },
      hideDatetimeIsWithin: {
        type: Boolean,
        default: false
      },
      outsideModal: {
        type: Boolean,
        default: false,
      },
      matchFormFields: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        ComparisonType,
        DatetimeComparison,
        DurationAndTimeHelper,
        referencedControl: null
      }
    },
    computed: {
      relativeUserText(){
        if (this.parent instanceof ListView){
          return "Current User"
        }else{
          return "Relative User"
        }
      },
      referencedControlId: {
        get(): string {
          return this.rule.referenced_control_id;
        },
        set(newVal: string) {
          this.rule.referenced_control_id = newVal;

          if (this.rule.builtInValueType) {
            this.referencedControl = this.rule.builtInReferencedValue;
          }else{
            this.referencedControl = this.parent.getReferencedControl(this.rule.referenced_control_id);
          }
          if (this.parent instanceof ListView){
            this.rule.setComparisonType(this.referencedControl.displayConditionType(this.rule.target_type, this.parent));
          }else{
            this.rule.setComparisonType(this.referencedControl.displayConditionType(this.rule.target_type, true));
          }
        }
      },
      comparisonType: {
        get(): ComparisonType {
          if (this.referencedControl) {
            return this.referencedControl.displayConditionType(this.rule.target_type, this.parent);
          }
          return null;
        },
        set(newType): void {
          this.rule.setComparisonType(newType);
        }
      },
      targetType: {
        get(): ComparisonType {
          return this.rule.target_type;
        },
        set(newType): void {
          this.rule.target_type = newType;
          this.rule.value = null;
        }
      },
      targetTypes(): Array<TargetType> {
        return this.referencedControl.targetTypes();
      },
      targetReferencedControlOptions(): IVOptionSetLocal{
        return {
          valueKey: 'client_id',
          labelKey: 'referenceNameOrDefault',
          options: this.parent.getTargetReferencableControls()
        }
      },
      recordExistsOptions(): IVOptionSetLocal{
        return {
          valueKey: 'client_id',
          labelKey: 'label',
          options: [{client_id: "exists", label: "Exists"}, {client_id: "does_not_exist", label: "Does Not Exist"}]
        }
      },
      accountRelationOptions(): IVOptionSetLocal {
        const options = ObjectModelRelationDefinition.accountRelationConditionalValueOptions();
        const selectedOption = options.find(o => {
          return NumberHelper.toIntOrNull(o.value) === NumberHelper.toIntOrNull(this.rule.value)
        })
        return {
          selectedOption,
          options
        }
      },
      dateTypeOptions(): IVOptionSetLocal {
        return {
          valueKey: 'client_id',
          labelKey: 'label',
          options: [{client_id: "static", label: "Static"}, {client_id: "relative", label: "Relative"}]
        }
      },
      relativeDateTypeOptions(): IVOptionSetLocal {
        return {
          valueKey: 'client_id',
          labelKey: 'label',
          options: [{client_id: "last", label: "Last"}, {client_id: "next", label: "Next"}]
        }
      },
      relativeDateOptions(): IVOptionSetLocal {
        return {
          valueKey: 'client_id',
          labelKey: 'label',
          options: [
            {client_id: "monday", label: "Monday"}, 
            {client_id: "tuesday", label: "Tuesday"},
            {client_id: "wednesday", label: "Wednesday"},
            {client_id: "thursday", label: "Thursday"},
            {client_id: "friday", label: "Friday"},
            {client_id: "saturday", label: "Saturday"},
            {client_id: "sunday", label: "Sunday"}
          ]
        }
      },
    },
    methods: {
      selectedUserChange(value): void {
        //@ts-ignore
        this.rule.value = value.userName;
        // currently setting the rule's referenced_value_id to the user's id as it may be helpful with the query
        // will remove if unnecessary
        //@ts-ignore
        this.rule.referenced_value_id = value.userId;
      }
    },
    created(){
      if (this.matchFormFields) {
        this.rule.is_reference = true
      }
      if (this.rule.referenced_control_id ) {
        if (this.rule.builtInValueType) {
          this.referencedControl = this.rule.builtInReferencedValue;
        } else {
          this.referencedControl = this.parent.getReferencedControl(this.rule.referenced_control_id);
        }
      }
    },
  });
</script>
